/**
 * Only add items here if they have been added in Revenuecat
 */
import { z } from 'zod'
import { EntityIdSchema } from '../ids'
import { AuditPropsSchema } from '../common'

export const TERROS_ENTITLEMENTS = [
  'canvass',
  'user_manage',
  'team_manage',
  'calendar',
  'setter_closer',
  'integrations',
  'videos',
  'chat',
  'recording',
  'gamification',
  'stats',
  'demo',
  'programs',
  'tasks',
  'dashboards',
  'recruiting',
  'account_assignment',
  'shadow',
  'one_on_one',
  'team_meeting',
  'compliance',
  'wirecenter_data',
] as const
export const TerrosEntitlementSchema = z.enum(TERROS_ENTITLEMENTS)
export type TerrosEntitlement = z.Infer<typeof TerrosEntitlementSchema>
export const isTerrosEntitlement = (entitlement: unknown): entitlement is TerrosEntitlement =>
  TerrosEntitlementSchema.safeParse(entitlement).success

export const CANVASS_MAX_ENTITLEMENTS: Entitlement[] = [
  'permit_data_home_addition',
  'permit_data_electrical_panel',
  'permit_data_pool',
  'permit_data_ev',
  'permit_data_adu',
  'permit_data_hvac',
  'permit_data_spa',
  'permit_data_electric_water_heater',
  'permit_data_roof',
  'property_last_sold_date',
  'resident_age',
  'resident_credit_score',
  'permit_data_solar',
  'recent_solar_permit_data',
  'bankrupt_installers',
  'finance_data',
]

export const CANVASS_PRO_ENTITLEMENTS: Entitlement[] = [
  'permit_data_home_addition',
  'permit_data_electrical_panel',
  'permit_data_pool',
  'permit_data_ev',
  'permit_data_adu',
  'permit_data_hvac',
  'permit_data_spa',
  'permit_data_electric_water_heater',
  'permit_data_roof',
  'property_last_sold_date',
  'resident_age',
  'resident_credit_score',
]

export const getEntitlementTags = (entitlements: Entitlement[]): string => {
  const set = new Set(entitlements)
  const tags: string[] = []
  if (CANVASS_MAX_ENTITLEMENTS.every((e) => set.has(e))) tags.push('canvass_max')
  if (CANVASS_PRO_ENTITLEMENTS.every((e) => set.has(e))) tags.push('canvass_pro')
  if (set.has('recording')) tags.push('recorder')
  if (set.has('recruiting')) tags.push('recruiting')
  if (set.has('weather_data')) tags.push('hail')
  return tags.join(',')
}

export const PERMIT_ENTITLEMENTS = [
  'permit_data_solar',
  'permit_data_roof',
  'permit_data_pool',
  'permit_data_ev',
  'permit_data_electric_water_heater',
  'permit_data_spa',
  'permit_data_home_addition',
  'permit_data_adu',
  'permit_data_hvac',
  'permit_data_electrical_panel',
] as const

export const API_ENTITLEMENTS = ['new_move_ins', 'permits'] as const
export const ApiEntitlementSchema = z.enum(API_ENTITLEMENTS)

export const PermitEntitlementSchema = z.enum(PERMIT_ENTITLEMENTS)
export type PermitEntitlement = z.Infer<typeof PermitEntitlementSchema>
export const isPermitEntitlement = (entitlement: Entitlement): entitlement is PermitEntitlement =>
  PERMIT_ENTITLEMENTS.some((e) => e === entitlement)

export const RESIDENT_ENTITLEMENTS = ['resident_age', 'resident_credit_score'] as const
export const ResidentEntitlementSchema = z.enum(RESIDENT_ENTITLEMENTS)
export type ResidentEntitlement = z.Infer<typeof ResidentEntitlementSchema>
export const isResidentEntitlement = (entitlement: Entitlement): entitlement is ResidentEntitlement =>
  RESIDENT_ENTITLEMENTS.some((e) => e === entitlement)

export const OTHER_ENTITLEMENTS = [
  'property_last_sold_date',
  'weather_data',
  'bankrupt_installers',
  'finance_data',
  'recent_solar_permit_data',
] as const
export const OtherEntitlementSchema = z.enum(OTHER_ENTITLEMENTS)
export type OtherEntitlement = z.Infer<typeof OtherEntitlementSchema>
export const isOtherEntitlement = (entitlement: Entitlement): entitlement is OtherEntitlement =>
  OTHER_ENTITLEMENTS.some((e) => e === entitlement)

export const IAPEntitlementSchema = z.union([
  PermitEntitlementSchema,
  ResidentEntitlementSchema,
  OtherEntitlementSchema,
])
export type IAPEntitlement = z.Infer<typeof IAPEntitlementSchema>
export const isIAPEntitlement = (entitlement: Entitlement): entitlement is IAPEntitlement =>
  isPermitEntitlement(entitlement) || isResidentEntitlement(entitlement) || isOtherEntitlement(entitlement)

export const EntitlementSchema = z.union([TerrosEntitlementSchema, IAPEntitlementSchema, ApiEntitlementSchema])
export type Entitlement = z.Infer<typeof EntitlementSchema>

/** Every value `EntitlementSchema` accepts, for docs and pickers. */
export const ENTITLEMENTS: Entitlement[] = [
  ...TERROS_ENTITLEMENTS,
  ...PERMIT_ENTITLEMENTS,
  ...RESIDENT_ENTITLEMENTS,
  ...OTHER_ENTITLEMENTS,
  ...API_ENTITLEMENTS,
]

export const TinyEntitlementSchema = z.object({
  ...AuditPropsSchema.shape,
  entitlement: EntitlementSchema,
  source: z.union([EntityIdSchema, z.literal('revenuecat'), z.literal('trial')]).meta({
    description:
      'companyId: inherited from company subscription (dynamodb); teamId: inherited from team subscription (dynamodb); userId: inherited from user subscription, typically manually added to dynamodb; revenuecat: directly from revenuecat (user only for now); trial: from feature flag',
  }),
  expiresAt: z.number().optional(),
})
export type TinyEntitlement = z.infer<typeof TinyEntitlementSchema>

export const TrialEntitlementSchema = z.object({
  entitlement: EntitlementSchema,
  expiresAt: z.number().optional(),
})
export type TrialEntitlement = z.infer<typeof TrialEntitlementSchema>
